import React, { useState, useEffect, useMemo } from 'react';
import { 
  FaFilter, 
  FaSearch, 
  FaSortAmountDown, 
  FaSortAmountUp,
  FaChartLine,
  FaRupeeSign,
  FaPhone,
  FaEnvelope,
  FaLock,
  FaSignInAlt,
  FaPercentage,
  FaShieldAlt,
  FaExclamationTriangle,
  FaSync,
  FaInfoCircle,
  FaTags,
  FaIndustry,
  FaArrowUp,
  FaArrowDown,
  FaCaretDown,
  FaCaretUp,
  FaCheckCircle,
  FaTimesCircle,
  FaBalanceScale,
  FaCalculator,
  FaCalendarAlt,
  FaEye,
  FaCrown,
  FaSeedling,
  FaStar,
  FaFire,
  FaTag,
  FaBriefcase,
  FaMoneyBillWave,
  FaImage,
  FaLink,
  FaChevronLeft,
  FaChevronRight,
  FaStepBackward,
  FaStepForward,
  FaTimes
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import axios from 'axios';

const FundamentalScreener = () => {
  // ==================== START: LOGIN PROTECTION LOGIC ====================
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingLogin, setCheckingLogin] = useState(true);
  const [userData, setUserData] = useState(null);
  const [contentAccess, setContentAccess] = useState({ FundamentalScreener: 0 });
  

    // Page metadata========================================================
  const [pageMetadata, setPageMetadata] = useState({
    page_names: 'IPO Research & Analysis',
    description: 'Comprehensive IPO data from SEBI Registered Research Analyst'
  });
  // Add this fetch function (around line 60-80)
  const fetchPageMetadata = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in'}/api/page-metadata/public/page6`);
      if (response.data) {
        setPageMetadata({
          page_names: response.data.page_name, // Using title from database
          description: response.data.description
        });
      }
    } catch (error) {
      console.error('Error fetching page metadata:', error);
      // Keep default values if fetch fails
    }
  };

  // Add this inside your existing useEffect or create a new one (around line 100-110)
  useEffect(() => {
    if (isLoggedIn && !checkingLogin && contentAccess.FundamentalScreener === 1) {
      fetchPageMetadata(); // Add this line
    }
  }, [isLoggedIn, checkingLogin, contentAccess.FundamentalScreener]);
  // Metadata End===========================================================



  const checkLoginStatus = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const userDataStr = localStorage.getItem('user');
      const loggedIn = !!(accessToken && userDataStr);
      setIsLoggedIn(loggedIn);
      
      if (loggedIn) {
        const user = JSON.parse(userDataStr);
        setUserData(user);
        await fetchContentAccess(user.id);
      } else {
        setUserData(null);
        setContentAccess({ FundamentalScreener: 0 });
      }
      
      setCheckingLogin(false);
    } catch (error) {
      console.error('Error checking login status:', error);
      setCheckingLogin(false);
    }
  };
  
  const fetchContentAccess = async (userId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';
      const response = await axios.get(`${apiUrl}/api/content-access/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.data.success) {
        setContentAccess(response.data.data);
      } else {
        setContentAccess({ FundamentalScreener: 0 });
      }
    } catch (error) {
      console.error('Error fetching content access:', error);
      if (error.response?.status === 404) {
        setContentAccess({ FundamentalScreener: 0 });
      }
    }
  };
  
  useEffect(() => {
    checkLoginStatus();
    
    const handleStorageChange = () => {
      checkLoginStatus();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  // ==================== END: LOGIN PROTECTION LOGIC ====================

  // ==================== ORIGINAL COMPONENT STATE ====================
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' }); // No default sort
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStock, setSelectedStock] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [columnHeaders, setColumnHeaders] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const [processedData, setProcessedData] = useState({ 
    stocks: [], 
    categories: [], 
    terms: [], 
    howToUse: [], 
    disclosures: [] 
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // API Configuration with Auto-refresh
  const API_URL = 'https://sheets.googleapis.com/v4/spreadsheets/1BMfNAz2aIVNJZDM4RzhRrI0LSlWjVte1NEqgoEBSkq4/values/new%20sheet?key=AIzaSyBBDGaGd_H-xl8tjzlIZC-Yx2QLMM5FVUA';

  useEffect(() => {
    if (isLoggedIn && !checkingLogin && contentAccess.FundamentalScreener === 1) {
      fetchScreenerData();
    } else {
      if (isLoggedIn && !checkingLogin) {
        setLoading(false);
      }
    }
  }, [isLoggedIn, checkingLogin, contentAccess.FundamentalScreener]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (isLoggedIn && !checkingLogin && contentAccess.FundamentalScreener === 1) {
      const intervalId = setInterval(() => {
        fetchScreenerData();
      }, 60000);
      
      return () => clearInterval(intervalId);
    }
  }, [isLoggedIn, checkingLogin, contentAccess.FundamentalScreener]);

  // Process data when data changes
  useEffect(() => {
    if (data && data.values) {
      const result = processData(data.values);
      setProcessedData(result);
      setColumnHeaders(result.columnHeaders);
      
      // Extract all unique column names from stocks
      const columns = new Set();
      result.stocks.forEach(stock => {
        if (stock.dynamicData) {
          Object.keys(stock.dynamicData).forEach(key => {
            if (key && key.trim() !== '' && !key.includes('DYNAMICDATA') && !key.includes('COLUMNHEADERS')) {
              columns.add(key);
            }
          });
        }
      });
      setAllColumns(Array.from(columns));
    }
  }, [data]);

  // Reset to first page when search or filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, sortConfig]);

  const fetchScreenerData = async () => {
    try {
      setLoading(true);
      console.log('Fetching fundamental screener data...');
      
      const response = await fetch(API_URL);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch data: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      console.log('Fundamental Screener API Response received');
      
      if (!result.values) {
        throw new Error('No data found in the response');
      }
      
      setData(result);
      setLastUpdated(new Date());
      setLoading(false);
      
    } catch (err) {
      console.error('Error fetching screener data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Helper function to check if category is valid
  const isValidCategory = (category) => {
    if (!category || typeof category !== 'string') return false;
    
    const categoryStr = category.toString().trim().toUpperCase();
    
    // Check for error values
    const errorPatterns = [
      '#DIV/0!',
      '#DIV/0',
      '#N/A',
      '#VALUE!',
      '#VALUE',
      '#REF!',
      '#REF',
      '#NAME?',
      '#NAME',
      '#NUM!',
      '#NUM',
      '#NULL!',
      '#NULL',
      'ERROR',
      'N/A',
      'NA',
      'NOT AVAILABLE'
    ];
    
    // Check if category contains any error pattern
    for (const pattern of errorPatterns) {
      if (categoryStr.includes(pattern)) {
        return false;
      }
    }
    
    // Check if it looks like a ticker symbol (contains colon like NSE:DEMO)
    if (categoryStr.includes(':') || 
        categoryStr.includes('BSE:') || 
        categoryStr.includes('NSE:')) {
      return false;
    }
    
    // Check if it's empty or just whitespace
    if (categoryStr === '' || categoryStr === '""' || categoryStr === "''") {
      return false;
    }
    
    // Check if it's a valid length (too short or too long might be invalid)
    if (categoryStr.length < 2 || categoryStr.length > 100) {
      return false;
    }
    
    // Check if it contains only special characters
    const specialCharRegex = /^[^a-zA-Z0-9]+$/;
    if (specialCharRegex.test(categoryStr)) {
      return false;
    }
    
    return true;
  };

  // Helper function to clean category name
  const cleanCategoryName = (category) => {
    if (!category || typeof category !== 'string') return 'UNCATEGORIZED';
    
    let cleanCategory = category.toString().trim().toUpperCase();
    
    // If category is invalid, return 'UNCATEGORIZED'
    if (!isValidCategory(cleanCategory)) {
      return 'UNCATEGORIZED';
    }
    
    // Standardize category names
    if (cleanCategory.includes('BEST BUY') || cleanCategory.includes('STRONG BUY')) {
      return 'BEST BUY';
    } else if (cleanCategory.includes('REASONABLE') || cleanCategory.includes('BUY')) {
      return 'REASONABLE BUY';
    } else if (cleanCategory.includes('AVERAGE') || cleanCategory.includes('HOLD')) {
      return 'AVERAGE';
    } else if (cleanCategory.includes('EXPENSIVE') && !cleanCategory.includes('VERY')) {
      return 'EXPENSIVE';
    } else if (cleanCategory.includes('VERY EXPENSIVE') || cleanCategory.includes('SELL')) {
      return 'VERY EXPENSIVE';
    } else if (cleanCategory.includes('UNDERVALUED')) {
      return 'UNDERVALUED';
    } else if (cleanCategory.includes('OVER VALUED') || cleanCategory.includes('OVERVALUED')) {
      return 'OVERVALUED';
    }
    
    // If it's a valid category but doesn't match standard patterns, keep it as is
    return cleanCategory;
  };

  // Enhanced Dynamic data processing with image URL support - PROCESS ALL RECORDS
  const processData = (values) => {
    if (!values || values.length < 2) {
      console.log('No data available for processing');
      return { 
        stocks: [], 
        categories: [], 
        terms: [], 
        howToUse: [], 
        disclosures: [], 
        columnHeaders: [] 
      };
    }

    let stocks = [];
    let categories = new Set(['all']);
    let headers = [];
    
    // Get headers from first row
    headers = values[0].map(h => h ? h.toString().trim() : '');
    console.log('Headers from sheet:', headers);
    
    // Process ALL records (starting from row 1)
    for (let i = 1; i < values.length; i++) {
      const row = values[i];
      if (!row || row.length === 0) continue;
      
      // Create complete stock object with ALL columns
      const stockObj = {};
      let companyName = '';
      let imageUrl = '';
      let hasValidData = false;
      
      // Map ALL columns dynamically
      headers.forEach((header, index) => {
        if (header) {
          let rawValue = row[index] ? row[index].toString().trim() : '';
          const headerLower = header.toLowerCase();
          
          // Store all values
          stockObj[header] = rawValue;
          
          // Check if this is an image URL
          if (rawValue.includes('http') && 
              (rawValue.includes('.jpg') || 
               rawValue.includes('.png') || 
               rawValue.includes('.jpeg'))) {
            imageUrl = rawValue;
          }
          
          // Store company name and check if we have valid data
          if ((headerLower.includes('company') || 
               headerLower.includes('name') || 
               headerLower.includes('ticker')) && 
              rawValue && !rawValue.includes('http')) {
            companyName = rawValue;
            // Check if company name is valid (not an error value)
            if (companyName && !isValidCategory(companyName) && isValidCompanyName(companyName)) {
              hasValidData = true;
            }
          }
        }
      });
      
      // If no company name from specific columns, try to find it
      if (!companyName) {
        // Check second column (typically ticker/company name)
        if (row[1] && !row[1].toString().includes('http')) {
          companyName = row[1].toString().trim();
        } else if (row[2] && !row[2].toString().includes('http')) {
          companyName = row[2].toString().trim();
        }
      }
      
      // Check if we have a valid company name
      if (!hasValidData && companyName) {
        hasValidData = isValidCompanyName(companyName);
      }
      
      // Skip this row if no valid company name
      if (!hasValidData) {
        console.log(`Skipping row ${i} - Invalid company name:`, companyName);
        continue;
      }
      
      // Parse numeric values with better error handling
      const parseNumeric = (value, defaultValue = 0) => {
        if (!value) return defaultValue;
        try {
          const str = value.toString().replace(/[%,₹]/g, '').replace(/,/g, '').trim();
          // Check for error values
          if (str.includes('#DIV/0') || str.includes('#N/A') || str.includes('#VALUE') || 
              str.includes('#REF') || str.includes('#NAME') || str.includes('#NUM') || 
              str.includes('#NULL') || str.toUpperCase() === 'N/A') {
            return defaultValue;
          }
          const num = parseFloat(str);
          return isNaN(num) ? defaultValue : num;
        } catch {
          return defaultValue;
        }
      };
      
      const cmp = parseNumeric(row[5]);
      const intrinsicValue = parseNumeric(row[6]);
      const bestBuyPrice = parseNumeric(row[8]);
      const marginOfSafetyRaw = parseNumeric(row[9]);
      const forecast5Y = parseNumeric(row[10]);
      
      // Parse expected return
      let expectedReturn = 0;
      if (row[11]) {
        expectedReturn = parseNumeric(row[11]);
      }
      
      // Get category (last column or try to find it)
      let category = '';
      // Try last column first
      if (row[12]) {
        category = row[12].toString().trim();
      } else if (row[row.length - 1]) {
        // Try the last non-empty cell
        category = row[row.length - 1].toString().trim();
      }
      
      // Clean category name and validate it
      let cleanCategory = cleanCategoryName(category);
      
      // Skip this stock if category is UNCATEGORIZED (invalid)
      if (cleanCategory === 'UNCATEGORIZED') {
        console.log(`Skipping stock ${companyName} - Invalid category:`, category);
        continue;
      }
      
      // Add to categories set
      categories.add(cleanCategory);
      
      // Calculate upside for display
      const upside = intrinsicValue > 0 && cmp > 0 ? 
        ((intrinsicValue - cmp) / cmp) * 100 : 0;
      
      stocks.push({
        id: i,
        sno: i,
        companyName: companyName.trim(),
        ticker: row[1] || '',
        imageUrl: imageUrl,
        cmp: cmp,
        intrinsicValue: intrinsicValue,
        bestBuyPrice: bestBuyPrice,
        marginOfSafety: marginOfSafetyRaw,
        forecast5Y: forecast5Y,
        expectedReturn: expectedReturn,
        category: cleanCategory,
        upside: parseFloat(upside.toFixed(2)),
        isUndervalued: intrinsicValue > cmp,
        rating: getRating(cleanCategory, marginOfSafetyRaw, expectedReturn),
        // Store ALL dynamic data
        dynamicData: stockObj,
        columnHeaders: headers,
        hasDynamicData: true,
        rawRow: row,
        originalIndex: i // Preserve original order
      });
    }

    // Sort stocks by original index to preserve API order
    stocks.sort((a, b) => a.originalIndex - b.originalIndex);

    console.log('Processed stocks:', stocks.length);
    console.log('Valid categories found:', Array.from(categories));
    
    // Sort categories in a logical order
    const categoryOrder = {
      'BEST BUY': 1,
      'REASONABLE BUY': 2,
      'UNDERVALUED': 3,
      'AVERAGE': 4,
      'EXPENSIVE': 5,
      'VERY EXPENSIVE': 6,
      'OVERVALUED': 7
    };
    
    const sortedCategories = Array.from(categories)
      .filter(cat => cat !== 'all' && cat !== 'UNCATEGORIZED')
      .sort((a, b) => {
        const priorityA = categoryOrder[a] || 99;
        const priorityB = categoryOrder[b] || 99;
        return priorityA - priorityB;
      });
    
    return { 
      stocks, 
      categories: sortedCategories,
      terms: [],
      howToUse: [],
      disclosures: [],
      columnHeaders: headers
    };
  };

  // Helper function to validate company name
  const isValidCompanyName = (companyName) => {
    if (!companyName || typeof companyName !== 'string') return false;
    
    const nameStr = companyName.toString().trim();
    
    // Check for error values
    const errorPatterns = [
      '#DIV/0!',
      '#DIV/0',
      '#N/A',
      '#VALUE!',
      '#VALUE',
      '#REF!',
      '#REF',
      '#NAME?',
      '#NAME',
      '#NUM!',
      '#NUM',
      '#NULL!',
      '#NULL',
      'ERROR',
      'N/A',
      'NA',
      'NOT AVAILABLE'
    ];
    
    // Check if company name contains any error pattern
    for (const pattern of errorPatterns) {
      if (nameStr.toUpperCase().includes(pattern)) {
        return false;
      }
    }
    
    // Check if it's empty or just whitespace
    if (nameStr === '' || nameStr === '""' || nameStr === "''") {
      return false;
    }
    
    // Check if it's a valid length
    if (nameStr.length < 2 || nameStr.length > 100) {
      return false;
    }
    
    // Check if it contains only special characters
    const specialCharRegex = /^[^a-zA-Z0-9]+$/;
    if (specialCharRegex.test(nameStr)) {
      return false;
    }
    
    return true;
  };

  // Clear all filters and sorting
  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setSortConfig({ key: null, direction: 'desc' });
  };

  // Get ALL dynamic fields for display (excluding already displayed ones)
  const getAllDisplayableFields = (stock) => {
    if (!stock.dynamicData) {
      return [];
    }
    
    // Fields already shown in main section
    const alreadyDisplayed = [
      'S.NO', 'S/NO', 'NO.', 'ID',
      'COMPANY', 'COMPANY NAME', 'NAME', 'COMPANY_NAME',
      'TICKER', 'SYMBOL',
      'IMAGE', 'IMAGE_URL', 'LOGO', 'URL', 'PICTURE',
      'CMP', 'CURRENT PRICE', 'MARKET PRICE',
      'INTRINSIC VALUE', 'INTRINSIC', 'FAIR VALUE',
      'BEST BUY', 'BEST BUY PRICE', 'BUY PRICE',
      'MARGIN OF SAFETY', 'MARGIN', 'SAFETY', 'SAFETY MARGIN',
      'FORECAST', '5Y', '5-YEAR', '5 YEAR', 'FUTURE PRICE',
      'EXPECTED RETURN', 'RETURN', 'ANNUAL RETURN', 'CAGR',
      'CATEGORY', 'RATING', 'CLASSIFICATION', 'STATUS',
      'ROWINDEX', 'DYNAMICDATA', 'COLUMNHEADERS', 'HASDYNAMICDATA'
    ];
    
    const allFields = [];
    
    Object.entries(stock.dynamicData).forEach(([key, value]) => {
      if (!key) return;
      
      const keyUpper = key.toUpperCase();
      
      // Skip if already displayed in main card
      const isExcluded = alreadyDisplayed.some(excluded => 
        keyUpper.includes(excluded.toUpperCase()) || keyUpper === excluded.toUpperCase()
      );
      
      // Skip empty values
      const isEmpty = value === undefined || value === null || 
                     value.toString().trim() === '' || 
                     value.toString().trim() === '""' ||
                     value.toString().trim() === '#N/A' ||
                     (typeof value === 'number' && value === 0);
      
      // Skip system fields
      const isSystemField = keyUpper.includes('ROWINDEX') || 
                           keyUpper === 'ID';
      
      // Skip error values
      const valueStr = value.toString().toUpperCase();
      const isErrorValue = valueStr.includes('#DIV/0') || 
                          valueStr.includes('#N/A') || 
                          valueStr.includes('#VALUE') ||
                          valueStr.includes('#REF') ||
                          valueStr.includes('#NAME') ||
                          valueStr.includes('#NUM') ||
                          valueStr.includes('#NULL') ||
                          valueStr === 'N/A' ||
                          valueStr === 'ERROR';
      
      if (!isExcluded && !isEmpty && !isSystemField && !isErrorValue) {
        // Determine field type
        const valueStr = value.toString();
        const isImageUrl = valueStr.includes('http') && 
                          (valueStr.includes('.jpg') || 
                           valueStr.includes('.png') || 
                           valueStr.includes('.jpeg') ||
                           valueStr.includes('.webp'));
        const isUrl = valueStr.includes('http') && !isImageUrl;
        const isNumeric = !isNaN(parseFloat(valueStr)) && !isImageUrl && !isUrl;
        const isPercentage = keyUpper.includes('%') || 
                           keyUpper.includes('PERCENT') || 
                           keyUpper.includes('RETURN') ||
                           keyUpper.includes('GROWTH') ||
                           keyUpper.includes('YIELD') ||
                           valueStr.includes('%');
        const isCurrency = (keyUpper.includes('PRICE') || 
                          keyUpper.includes('VALUE') || 
                          keyUpper.includes('AMOUNT') || 
                          keyUpper.includes('₹') || 
                          keyUpper.includes('INR') ||
                          keyUpper.includes('RS') ||
                          keyUpper.includes('RUPEES')) && 
                          !isPercentage;
        
        const displayName = formatHeaderName(key);
        
        allFields.push({
          key,
          displayName,
          value: isNumeric ? parseFloat(valueStr) : valueStr,
          rawValue: valueStr,
          isNumeric,
          isPercentage,
          isCurrency,
          isImageUrl,
          isUrl,
          isEmpty: false
        });
      }
    });
    
    return allFields;
  };

  // Format header names for display
  const formatHeaderName = (header) => {
    return header
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/\./g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, l => l.toUpperCase())
      .replace('Field ', 'Additional Info ');
  };

  // Format value based on field type
  const formatValue = (field) => {
    if (field.isImageUrl) {
      return field.value;
    } else if (field.isUrl) {
      return field.value;
    } else if (field.isNumeric) {
      if (field.isPercentage) {
        const valueStr = field.rawValue.toString();
        if (valueStr.includes('%')) {
          return valueStr;
        }
        return `${field.value >= 0 ? '+' : ''}${field.value.toFixed(2)}%`;
      } else if (field.isCurrency) {
        return `₹${field.value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      } else {
        if (Number.isInteger(field.value)) {
          return field.value.toLocaleString('en-IN');
        } else {
          return field.value.toFixed(2);
        }
      }
    }
    return field.value.toString();
  };

  // Render field value based on type
  const renderFieldValue = (field) => {
    if (field.isImageUrl) {
      return (
        <div className="mt-1">
          <img 
            src={field.value} 
            alt={field.displayName}
            className="w-16 h-16 object-cover rounded-lg border border-gray-200"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://via.placeholder.com/64?text=Logo';
            }}
          />
          <div className="text-xs text-gray-500 mt-1 truncate">Image URL</div>
        </div>
      );
    } else if (field.isUrl) {
      return (
        <div className="mt-1">
          <a 
            href={field.value} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1 truncate"
          >
            <FaLink className="text-xs" />
            <span className="truncate">Open Link</span>
          </a>
          <div className="text-xs text-gray-500 mt-1 truncate">{field.value}</div>
        </div>
      );
    } else {
      return (
        <div className={`text-lg font-bold ${
          field.isPercentage 
            ? field.value >= 0 ? 'text-green-600' : 'text-red-600'
            : field.isCurrency ? 'text-blue-700' : 'text-gray-900'
        }`}>
          {formatValue(field)}
        </div>
      );
    }
  };

  const getRating = (category, marginOfSafety, expectedReturn) => {
    switch(category) {
      case 'BEST BUY':
        return '⭐⭐⭐⭐⭐';
      case 'REASONABLE BUY':
        return '⭐⭐⭐⭐';
      case 'AVERAGE':
        return '⭐⭐⭐';
      case 'EXPENSIVE':
        return '⭐⭐';
      case 'VERY EXPENSIVE':
        return '⭐';
      default:
        if (marginOfSafety > 20 && expectedReturn > 15) return '⭐⭐⭐⭐';
        if (marginOfSafety > 0 && expectedReturn > 10) return '⭐⭐⭐';
        if (expectedReturn > 0) return '⭐⭐';
        return '⭐';
    }
  };

  const { stocks, categories } = processedData;

  // Filter and sort ALL stocks - preserve original order when no sort is applied
  const filteredAndSortedStocks = useMemo(() => {
    let filtered = [...stocks];
    
    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(stock => stock.category === selectedCategory);
    }
    
    // Apply search filter (works on ALL records)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(stock => 
        stock.companyName.toLowerCase().includes(term) ||
        (stock.ticker && stock.ticker.toLowerCase().includes(term)) ||
        (stock.category && stock.category.toLowerCase().includes(term))
      );
    }
    
    // Apply sorting only if a sort key is selected
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        
        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    } else {
      // Preserve original order when no sort is applied
      filtered.sort((a, b) => a.originalIndex - b.originalIndex);
    }
    
    return filtered;
  }, [stocks, selectedCategory, searchTerm, sortConfig]);

  // Calculate statistics for ALL filtered stocks
  const stats = useMemo(() => {
    if (filteredAndSortedStocks.length === 0) return null;
    
    const totalStocks = filteredAndSortedStocks.length;
    const bestBuyCount = filteredAndSortedStocks.filter(s => s.category === 'BEST BUY').length;
    const reasonableBuyCount = filteredAndSortedStocks.filter(s => s.category === 'REASONABLE BUY').length;
    const avgMarginOfSafety = filteredAndSortedStocks.reduce((sum, s) => sum + s.marginOfSafety, 0) / totalStocks;
    const avgExpectedReturn = filteredAndSortedStocks.reduce((sum, s) => sum + s.expectedReturn, 0) / totalStocks;
    const undervaluedCount = filteredAndSortedStocks.filter(s => s.isUndervalued).length;
    
    return {
      totalStocks,
      bestBuyCount,
      reasonableBuyCount,
      avgMarginOfSafety: parseFloat(avgMarginOfSafety.toFixed(2)),
      avgExpectedReturn: parseFloat(avgExpectedReturn.toFixed(2)),
      undervaluedCount,
      overvaluedCount: totalStocks - undervaluedCount
    };
  }, [filteredAndSortedStocks]);

  // Pagination calculations
  const totalPages = useMemo(() => {
    return Math.ceil(filteredAndSortedStocks.length / itemsPerPage);
  }, [filteredAndSortedStocks, itemsPerPage]);

  // Get current page stocks
  const currentPageStocks = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredAndSortedStocks.slice(startIndex, endIndex);
  }, [filteredAndSortedStocks, currentPage, itemsPerPage]);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      
      if (endPage - startPage + 1 < maxPagesToShow) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
    }
    
    return pageNumbers;
  };

  // Handle sort click
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Pagination handlers
  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToFirstPage = () => goToPage(1);
  const goToLastPage = () => goToPage(totalPages);
  const goToPreviousPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return '₹0.00';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Format percentage
  const formatPercentage = (value) => {
    if (!value && value !== 0) return '0.00';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value.toString();
    return `${numValue >= 0 ? '+' : ''}${numValue.toFixed(2)}%`;
  };

  // Get category color
  const getCategoryColor = (category) => {
    switch(category) {
      case 'BEST BUY': return 'bg-green-100 text-green-800 border-green-200';
      case 'REASONABLE BUY': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'AVERAGE': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'EXPENSIVE': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'VERY EXPENSIVE': return 'bg-red-100 text-red-800 border-red-200';
      case 'UNDERVALUED': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'OVERVALUED': return 'bg-rose-100 text-rose-800 border-rose-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Get category background color for banners
  const getCategoryBgColor = (category) => {
    switch(category) {
      case 'BEST BUY': return 'bg-gradient-to-r from-green-500 to-green-600';
      case 'REASONABLE BUY': return 'bg-gradient-to-r from-blue-500 to-blue-600';
      case 'AVERAGE': return 'bg-gradient-to-r from-amber-500 to-amber-600';
      case 'EXPENSIVE': return 'bg-gradient-to-r from-orange-500 to-orange-600';
      case 'VERY EXPENSIVE': return 'bg-gradient-to-r from-red-500 to-red-600';
      case 'UNDERVALUED': return 'bg-gradient-to-r from-emerald-500 to-emerald-600';
      case 'OVERVALUED': return 'bg-gradient-to-r from-rose-500 to-rose-600';
      default: return 'bg-gradient-to-r from-gray-500 to-gray-600';
    }
  };

  // ==================== LOGIN PROTECTION RENDER LOGIC ====================
  if (checkingLogin) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white relative">
          <div className="absolute inset-0 backdrop-blur-[5px] bg-white/80 z-10 flex items-center justify-center">
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-200"
            >
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-r from-blue-100 to-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaLock className="text-3xl text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Authentication Required</h2>
                <p className="text-gray-600 mb-1">You need to log in to access the Fundamental Screener.</p>
                <p className="text-sm text-gray-500 mb-6">This content is exclusive to registered members.</p>
                <Link to="/login">
                  <button className="w-full py-3.5 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3">
                    <FaSignInAlt className="text-lg" />
                    Go to Login Page
                  </button>
                </Link>
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <p className="text-sm text-gray-500">
                    Don't have an account?{' '}
                    <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
                      Sign up here
                    </Link>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
          <div className="opacity-40 pointer-events-none">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col md:flex-row items-center justify-between">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold mb-2">Fundamental Stock Screener</h1>
                    <p className="text-blue-100">Intrinsic value based stock valuation tool</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="text-center py-20">
                <p className="text-gray-400">Content blurred for privacy. Please login to view.</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }
  
  if (contentAccess.FundamentalScreener !== 1) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white relative">
          <div className="absolute inset-0 backdrop-blur-[5px] bg-white/80 z-10 flex items-center justify-center">
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 border border-gray-200"
            >
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-r from-amber-100 to-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FaLock className="text-3xl text-amber-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Content Access Restricted</h2>
                <p className="text-gray-600 mb-3">
                  Hello <span className="font-semibold text-blue-600">{userData?.username}</span>! 
                  You don't have access to the Fundamental Screener yet.
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  This premium content requires special access permissions.
                </p>
                <div className="space-y-3">
                  <a 
                    href="tel:+919319745997"
                    className="w-full py-3.5 px-6 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3"
                  >
                    <FaPhone className="text-lg" />
                    Call Us: +91 9319745997
                  </a>
                  <a 
                    href="mailto:support@marketmaestroo.com"
                    className="w-full py-3.5 px-6 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-3"
                  >
                    <FaEnvelope className="text-lg" />
                    Email: support@marketmaestroo.com
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
          <div className="opacity-40 pointer-events-none">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col md:flex-row items-center justify-between">
                  <div>
                    <h1 className="text-3xl md:text-4xl font-bold mb-2">Fundamental Stock Screener</h1>
                    <p className="text-blue-100">Intrinsic value based stock valuation tool</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="text-center py-20">
                <p className="text-gray-400">Content access restricted.</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // ==================== MAIN RENDER LOGIC ====================
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading Fundamental Screener...</p>
          <p className="text-sm text-gray-500 mt-2">Auto-refresh every 60 seconds</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg border border-red-100 max-w-md">
          <FaExclamationTriangle className="text-red-500 text-4xl mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Screener</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={fetchScreenerData}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 mx-auto"
          >
            <FaSync />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold mb-2">{pageMetadata.page_names}</h1>
                <p className="text-blue-100">{pageMetadata.description}</p>
              </div>
              <div className="mt-4 md:mt-0 flex flex-col md:flex-row items-center gap-4">
                <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                  <FaCalculator className="text-yellow-300" />
                  <span className="font-semibold">Valuation Tool</span>
                </div>
                <button
                  onClick={fetchScreenerData}
                  className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
                >
                  <FaSync className="text-sm" />
                  Refresh Now
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

          {/* Search and Filter Controls */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="flex-1">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search stocks by name, ticker, or category..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <FaTimesCircle />
                    </button>
                  )}
                </div>
              </div>
              
              {/* Category Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="w-full md:w-auto px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-between gap-2"
                >
                  <div className="flex items-center gap-2">
                    <FaTags />
                    <span>
                      {selectedCategory === 'all' ? 'All Categories' : selectedCategory}
                    </span>
                  </div>
                  {showDropdown ? <FaCaretUp /> : <FaCaretDown />}
                </button>
                
                <AnimatePresence>
                  {showDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute z-10 mt-2 w-full md:w-64 bg-white border border-gray-200 rounded-lg shadow-lg py-2 max-h-96 overflow-y-auto"
                    >
                      <button
                        onClick={() => {
                          setSelectedCategory('all');
                          setShowDropdown(false);
                        }}
                        className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between ${
                          selectedCategory === 'all' ? 'bg-blue-50 text-blue-700' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full bg-gray-400"></span>
                          <span className="font-medium">All Categories</span>
                        </div>
                        <span className="text-sm text-gray-500">
                          {stocks.length} stocks
                        </span>
                      </button>
                      
                      {categories.map((category) => (
                        <button
                          key={category}
                          onClick={() => {
                            setSelectedCategory(category);
                            setShowDropdown(false);
                          }}
                          className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between ${
                            selectedCategory === category ? 'bg-blue-50 text-blue-700' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`w-3 h-3 rounded-full ${
                              category === 'BEST BUY' ? 'bg-green-500' :
                              category === 'REASONABLE BUY' ? 'bg-blue-500' :
                              category === 'AVERAGE' ? 'bg-amber-500' :
                              category === 'EXPENSIVE' ? 'bg-orange-500' :
                              category === 'VERY EXPENSIVE' ? 'bg-red-500' :
                              category === 'UNDERVALUED' ? 'bg-emerald-500' :
                              'bg-rose-500'
                            }`}></span>
                            <span className="font-medium">{category}</span>
                          </div>
                          <span className="text-sm text-gray-500">
                            {stocks.filter(s => s.category === category).length} stocks
                          </span>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Clear Filters Button */}
              <button
                onClick={clearAllFilters}
                className="px-4 py-3 rounded-lg transition-colors flex items-center gap-2 bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300"
              >
                <FaTimes />
                Clear Filters
              </button>
            </div>

            {/* Sort Controls */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
              <span className="text-gray-600 font-medium">Sort by:</span>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleSort('marginOfSafety')}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    sortConfig.key === 'marginOfSafety' 
                      ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  Margin of Safety
                  {sortConfig.key === 'marginOfSafety' && (
                    sortConfig.direction === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />
                  )}
                </button>
                <button
                  onClick={() => handleSort('expectedReturn')}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    sortConfig.key === 'expectedReturn' 
                      ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  Expected Return
                  {sortConfig.key === 'expectedReturn' && (
                    sortConfig.direction === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />
                  )}
                </button>
                <button
                  onClick={() => handleSort('cmp')}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    sortConfig.key === 'cmp' 
                      ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  Current Price
                  {sortConfig.key === 'cmp' && (
                    sortConfig.direction === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />
                  )}
                </button>
                <button
                  onClick={() => handleSort('category')}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    sortConfig.key === 'category' 
                      ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-300'
                  }`}
                >
                  Category
                  {sortConfig.key === 'category' && (
                    sortConfig.direction === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />
                  )}
                </button>
              </div>
              
              {/* Clear Sort Button - only show when sorting is active */}
              {sortConfig.key && (
                <button
                  onClick={() => setSortConfig({ key: null, direction: 'desc' })}
                  className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                >
                  <FaTimes className="text-xs" />
                  Clear Sort
                </button>
              )}
              
              <div className="ml-auto text-sm text-gray-500">
                {filteredAndSortedStocks.length} stocks found • Page {currentPage} of {totalPages}
              </div>
            </div>
          </div>

          {/* Stock Cards - Card Design */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            

            {filteredAndSortedStocks.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl shadow-sm border border-gray-200">
                <FaFilter className="text-gray-400 text-4xl mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Valid Stocks Found</h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm 
                    ? `No valid stocks found matching "${searchTerm}" in ${selectedCategory === 'all' ? 'all categories' : selectedCategory}`
                    : `No valid stocks found in ${selectedCategory === 'all' ? 'any category' : selectedCategory}`
                  }
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Note: Stocks with error categories (#DIV/0, #N/A, #VALUE, etc.) are automatically filtered out.
                </p>
                <button 
                  onClick={clearAllFilters}
                  className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <>
                {/* Category Info Banner */}
                {selectedCategory !== 'all' && (
                  <div className={`${getCategoryBgColor(selectedCategory)} p-4 rounded-xl mb-6 flex items-center justify-between text-white`}>
                    <div className="flex items-center gap-3">
                      <FaTags className="text-white" />
                      <div>
                        <h3 className="text-xl font-bold">{selectedCategory}</h3>
                        <p className="text-white/80 text-sm">
                          Showing {currentPageStocks.length} of {filteredAndSortedStocks.length} stocks
                        </p>
                      </div>
                    </div>
                    <div className="text-white/90">
                      <div className="text-sm">Avg. Margin of Safety</div>
                      <div className="font-bold">
                        {filteredAndSortedStocks.length > 0 
                          ? `${(filteredAndSortedStocks.reduce((sum, s) => sum + s.marginOfSafety, 0) / filteredAndSortedStocks.length).toFixed(2)}`
                          : '0.00'}
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {currentPageStocks.map((stock, index) => {
                    const dynamicFields = getAllDisplayableFields(stock);
                    const globalIndex = (currentPage - 1) * itemsPerPage + index + 1;
                    
                    return (
                      <motion.div
                        key={stock.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        whileHover={{ y: -4 }}
                        className="group relative"
                      >
                        {/* Stock Card */}
                        <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl border border-gray-200 transition-all duration-300 overflow-hidden hover:border-blue-300 hover:shadow-xl h-full">
                         

                        

                          <div className="p-5">
                            {/* Stock Header with Image */}
                            <div className="flex items-start gap-3 mb-4">
                              {stock.imageUrl && (
                                <div className="flex-shrink-0">
                                  <img 
                                    src={stock.imageUrl} 
                                    alt={stock.companyName}
                                    className="w-14 h-14 object-cover rounded-lg border border-gray-200"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = 'https://via.placeholder.com/56?text=Logo';
                                    }}
                                  />
                                </div>
                              )}
                              <div className="flex-1">
                                <h3 className="font-bold text-gray-900 text-lg line-clamp-2 group-hover:text-blue-600 transition-colors">
                                  {stock.companyName}
                                </h3>
                                {stock.ticker && (
                                  <p className="text-sm text-gray-500 mt-1">{stock.ticker}</p>
                                )}
                              </div>
                            </div>

                            {/* Rating Stars */}
                            <div className="flex items-center justify-between mb-4">
                              <div className="text-yellow-500">{stock.rating}</div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(stock.category)}`}>
                                {stock.category}
                              </span>
                            </div>

                            {/* Core Metrics Section */}
                            <div className="mb-4">
                              <div className="grid grid-cols-2 gap-3 mb-3">
                                <div className="bg-gray-50 p-3 rounded-lg">
                                  <div className="text-xs text-gray-500 mb-1">Current Price</div>
                                  <div className="font-bold text-gray-900 text-lg">{formatCurrency(stock.cmp)}</div>
                                </div>
                                <div className="bg-green-50 p-3 rounded-lg">
                                  <div className="text-xs text-green-600 mb-1">Intrinsic Value</div>
                                  <div className="font-bold text-green-700 text-lg">{formatCurrency(stock.intrinsicValue)}</div>
                                </div>
                              </div>

                              {/* Return Metrics */}
                              <div className={`p-3 rounded-lg ${stock.isUndervalued ? 'bg-green-50' : 'bg-red-50'}`}>
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium text-gray-700">Margin of Safety</span>
                                  <div className={`text-lg font-bold ${stock.isUndervalued ? 'text-green-600' : 'text-red-600'} flex items-center gap-1`}>
                                    {stock.isUndervalued ? <FaArrowUp className="text-sm" /> : <FaArrowDown className="text-sm" />}
                                    {stock.isUndervalued ? '+' : ''}{stock.marginOfSafety.toFixed(2)}
                                  </div>
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                  Expected Return: {stock.expectedReturn >= 0 ? '+' : ''}{stock.expectedReturn.toFixed(2)}%
                                </div>
                              </div>
                            </div>

                            {/* Additional Data Section */}
                            {dynamicFields.filter(field => !field.isImageUrl && !field.isUrl).length > 0 && (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="text-sm font-semibold text-gray-700">Additional Data</h4>
                                  <span className="text-xs text-gray-500">
                                    {dynamicFields.length} fields
                                  </span>
                                </div>
                                
                                <div className="space-y-3 max-h-48 overflow-y-auto pr-2">
                                  {dynamicFields
                                    .filter(field => !field.isImageUrl && !field.isUrl)
                                    .slice(0, 3)
                                    .map((field, idx) => (
                                    <div 
                                      key={idx} 
                                      className={`p-2.5 rounded-lg ${field.isPercentage || field.isCurrency ? 'bg-blue-50' : 'bg-gray-50'}`}
                                    >
                                      <div className="justify-between items-start">
                                        <div className="flex-2">
                                          <div className="text-xs font-medium text-gray-600 mb-0.5">
                                            {field.displayName}
                                          </div>
                                          <div className={`text-sm font-bold ${
                                            field.isPercentage 
                                              ? field.value >= 0 ? 'text-green-600' : 'text-red-600'
                                              : field.isCurrency ? 'text-blue-700' : 'text-gray-900'
                                          }`}>
                                            {formatValue(field)}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                  {dynamicFields.filter(field => !field.isImageUrl && !field.isUrl).length > 3 && (
                                    <div className="text-center">
                                      <button
                                        onClick={() => setSelectedStock(stock)}
                                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                                      >
                                        + {dynamicFields.length - 3} more fields
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Best Buy Price */}
                            {stock.bestBuyPrice > 0 && (
                              <div className="mt-4 pt-3 border-t border-gray-200">
                                <div className="flex items-center justify-between text-sm">
                                  <div className="flex items-center gap-2 text-gray-600">
                                    <FaMoneyBillWave className="text-gray-400 text-xs" />
                                    <span>Best Buy Price</span>
                                  </div>
                                  <span className="font-medium text-blue-700">{formatCurrency(stock.bestBuyPrice)}</span>
                                </div>
                              </div>
                            )}

                            {/* 5-Year Forecast */}
                            {stock.forecast5Y > 0 && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <div className="flex items-center justify-between text-sm">
                                  <div className="flex items-center gap-2 text-gray-600">
                                    <FaChartLine className="text-gray-400 text-xs" />
                                    <span>5-Year Forecast</span>
                                  </div>
                                  <span className="font-medium text-amber-700">{formatCurrency(stock.forecast5Y)}</span>
                                </div>
                              </div>
                            )}

                            {/* Footer */}
                            <div className="mt-4 pt-3 border-t border-gray-100">
                              <div className="flex items-center justify-between text-xs text-gray-500">
                                <span>Position: {globalIndex}</span>
                                <span className={`px-2 py-0.5 rounded-full ${
                                  stock.expectedReturn > 20 ? 'bg-green-100 text-green-800' :
                                  stock.expectedReturn > 15 ? 'bg-blue-100 text-blue-800' :
                                  stock.expectedReturn > 10 ? 'bg-purple-100 text-purple-800' :
                                  stock.expectedReturn > 5 ? 'bg-amber-100 text-amber-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {stock.expectedReturn > 20 ? 'Exceptional' :
                                   stock.expectedReturn > 15 ? 'Excellent' :
                                   stock.expectedReturn > 10 ? 'Good' :
                                   stock.expectedReturn > 5 ? 'Fair' : 'Poor'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4"
                  >
                    <div className="text-sm text-gray-600">
                      Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedStocks.length)} of {filteredAndSortedStocks.length} stocks
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {/* First Page */}
                      <button
                        onClick={goToFirstPage}
                        disabled={currentPage === 1}
                        className={`p-2 rounded-lg ${
                          currentPage === 1 
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <FaStepBackward />
                      </button>
                      
                      {/* Previous Page */}
                      <button
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className={`p-2 rounded-lg ${
                          currentPage === 1 
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <FaChevronLeft />
                      </button>
                      
                      {/* Page Numbers */}
                      <div className="flex items-center gap-1">
                        {getPageNumbers().map((pageNumber) => (
                          <button
                            key={pageNumber}
                            onClick={() => goToPage(pageNumber)}
                            className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                              currentPage === pageNumber
                                ? 'bg-blue-600 text-white font-semibold'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {pageNumber}
                          </button>
                        ))}
                        
                        {totalPages > 5 && currentPage < totalPages - 2 && (
                          <>
                            <span className="px-2 text-gray-400">...</span>
                            <button
                              onClick={() => goToPage(totalPages)}
                              className={`w-10 h-10 flex items-center justify-center rounded-lg ${
                                currentPage === totalPages
                                  ? 'bg-blue-600 text-white font-semibold'
                                  : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {totalPages}
                            </button>
                          </>
                        )}
                      </div>
                      
                      {/* Next Page */}
                      <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className={`p-2 rounded-lg ${
                          currentPage === totalPages 
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <FaChevronRight />
                      </button>
                      
                      {/* Last Page */}
                      <button
                        onClick={goToLastPage}
                        disabled={currentPage === totalPages}
                        className={`p-2 rounded-lg ${
                          currentPage === totalPages 
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <FaStepForward />
                      </button>
                    </div>
                    
                    <div className="text-sm text-gray-600">
                      Page {currentPage} of {totalPages}
                    </div>
                  </motion.div>
                )}
              </>
            )}
          </motion.div>

          {/* Stock Detail Modal */}
          <AnimatePresence>
            {selectedStock && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
                onClick={() => setSelectedStock(null)}
              >
                <motion.div
                  initial={{ scale: 0.9, y: 50 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.9, y: 50 }}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-white rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-300 shadow-2xl"
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-start gap-4">
                      {selectedStock.imageUrl && (
                        <div className="flex-shrink-0">
                          <img 
                            src={selectedStock.imageUrl} 
                            alt={selectedStock.companyName}
                            className="w-20 h-20 object-cover rounded-xl border border-gray-200"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://via.placeholder.com/80?text=Logo';
                            }}
                          />
                        </div>
                      )}
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">{selectedStock.companyName}</h3>
                        {selectedStock.ticker && (
                          <p className="text-gray-600 mt-1">{selectedStock.ticker}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <FaStar className="text-yellow-500" />
                          <span className="text-sm text-gray-500">{selectedStock.rating}</span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedStock(null)}
                      className="text-gray-400 hover:text-gray-600 text-2xl"
                    >
                      ×
                    </button>
                  </div>

                  <div className="space-y-6">
                    {/* Core Metrics Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-500">Current Market Price</p>
                        <p className="text-xl font-bold text-gray-900">{formatCurrency(selectedStock.cmp)}</p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-700">Intrinsic Value</p>
                        <p className="text-xl font-bold text-green-700">{formatCurrency(selectedStock.intrinsicValue)}</p>
                      </div>
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-700">Best Buy Price</p>
                        <p className="text-xl font-bold text-blue-700">{formatCurrency(selectedStock.bestBuyPrice)}</p>
                      </div>
                      <div className="bg-amber-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-700">5-Year Forecast</p>
                        <p className="text-xl font-bold text-amber-700">{formatCurrency(selectedStock.forecast5Y)}</p>
                      </div>
                    </div>

                    {/* Return Metrics */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className={`p-4 rounded-lg ${selectedStock.marginOfSafety > 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700 font-medium">Margin of Safety</span>
                          <span className={`text-xl font-bold ${selectedStock.marginOfSafety > 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {formatPercentage(selectedStock.marginOfSafety)}
                          </span>
                        </div>
                      </div>

                      <div className={`p-4 rounded-lg ${selectedStock.expectedReturn > 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700 font-medium">Expected Return</span>
                          <span className={`text-xl font-bold ${selectedStock.expectedReturn > 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {formatPercentage(selectedStock.expectedReturn)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Category and Upside */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-500">Category</p>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mt-1 ${getCategoryColor(selectedStock.category)}`}>
                          {selectedStock.category}
                        </span>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-500">Upside Potential</p>
                        <p className={`text-lg font-bold ${selectedStock.upside > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatPercentage(selectedStock.upside)}
                        </p>
                      </div>
                    </div>

                    {/* Show all dynamic data in modal */}
                    {getAllDisplayableFields(selectedStock).length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <h4 className="text-lg font-semibold text-gray-800 mb-4">Complete Data Analysis</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {getAllDisplayableFields(selectedStock).map((field, idx) => (
                            <div 
                              key={idx} 
                              className={`p-4 rounded-lg ${
                                field.isImageUrl || field.isUrl ? 'bg-purple-50' :
                                field.isPercentage ? 'bg-green-50' :
                                field.isCurrency ? 'bg-blue-50' :
                                'bg-gray-50'
                              }`}
                            >
                              <div className="text-sm font-medium text-gray-600 mb-1">
                                {field.displayName}
                              </div>
                              {renderFieldValue(field)}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Additional Info */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="text-sm text-gray-500 grid grid-cols-2 gap-4">
                        <div>
                          <span className="font-medium">Stock ID:</span> {selectedStock.id}
                        </div>
                        <div>
                          <span className="font-medium">Global Position:</span> {filteredAndSortedStocks.findIndex(s => s.id === selectedStock.id) + 1}
                        </div>
                        <div className="col-span-2">
                          <span className="font-medium">Total Columns:</span> {selectedStock.columnHeaders.length}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        onClick={() => setSelectedStock(null)}
                        className="flex-1 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors font-medium"
                      >
                        Close Details
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-gray-500">
            {lastUpdated && (
              <p className="mt-1">Last updated: {lastUpdated.toLocaleString()}</p>
            )}
            <p className="mt-2 text-xs bg-gray-100 inline-block px-3 py-1 rounded-full">
              ANKIT MAESTROO IMPOSSIBLE TOOL (FIRST IN INDIA)
            </p>
            <p className="mt-1 text-xs text-green-600 bg-green-50 inline-block px-3 py-1 rounded-full">
              ✓ Showing only valid stocks (error categories filtered out)
            </p>
            {columnHeaders.length > 0 && (
              <div className="mt-3 text-xs text-gray-400">
                <p>Displaying {columnHeaders.length} columns from Google Sheets</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default FundamentalScreener;